import * as Sentry from "@sentry/angular";
import { environment } from "../../../environments/environment";

const graphQLUrl = environment.serverUrl + environment.graphQLEndpoint;

/**
 * The environment to report under.
 *
 * `environment.ts` is both what `ng serve` uses and what the dev site is built from, so the build
 * alone cannot tell a developer's machine from dev.circlesquare.app. The host can, and `local` is
 * what manager and tournament registration call it — errors from a laptop are then filtered out of
 * the dev environment rather than mixed into it.
 */
export function resolveSentryEnvironment(
  hostname = typeof window === 'undefined' ? '' : window.location.hostname,
): string {
  const isLocal =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname === '::1' ||
    hostname.endsWith('.local');

  return isLocal ? 'local' : environment.sentryEnvironment;
}

/**
 * Initializes Sentry error tracking.
 *
 * Called from `main.ts` before the app is bootstrapped so that errors thrown during
 * bootstrapping are reported as well. Only runs in the browser, the SSR entry point
 * (`main.server.ts`) never calls it.
 *
 * An empty `sentryDsn` disables the SDK completely. Local development does report, tagged as the
 * `local` environment the same way manager and tournament registration do.
 */
export function initSentry(): void {
  Sentry.init({
    dsn: environment.sentryDsn,
    enabled: !!environment.sentryDsn,
    environment: resolveSentryEnvironment(),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        networkDetailAllowUrls: [graphQLUrl],
        // Bodies stay out of replays. `networkDetailAllowUrls` defaults to capturing them, and the
        // GraphQL endpoint carries credentials both ways: `idToken` and `emailVerificationCode` go
        // up on the login mutations, and the access and refresh tokens come back down. Timing,
        // status and URL are what is actually useful for debugging, and those are still recorded.
        networkCaptureBodies: false,
        // Page text is recorded, as on manager and tournament registration — a replay of an empty
        // grey layout answers nothing. Anything too sensitive to record carries `sentry-mask`.
        maskAllText: false,
        maskAllInputs: true,
      }),
      Sentry.httpClientIntegration({
        failedRequestTargets: [graphQLUrl, /\/graphql$/],
      }),
      Sentry.graphqlClientIntegration({
        endpoints: [graphQLUrl, /\/graphql$/],
      }),
      Sentry.captureConsoleIntegration({
        levels: ['error'],
      }),
    ],
    tracesSampleRate: environment.sentryTracesSampleRate,
    tracePropagationTargets: [environment.serverUrl],
    replaysSessionSampleRate: environment.sentryReplaysSessionSampleRate,
    replaysOnErrorSampleRate: environment.sentryReplaysOnErrorSampleRate,
  });
}
